import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { SignIn } from './SignIn';
import { SignUp } from './SignUp';
import { ForgotPassword } from './ForgotPassword';
import { ResetPassword } from './ResetPassword';
import { supabase } from '../../lib/supabase';
import { consumeRedirectUrl } from '../../lib/authRedirect';
import { handleNewUser } from '../../hooks/handle_new_user';
import { sendWelcomeEmail } from '../../lib/email';

async function ensureEmailUserProfile(user: any) {
  try {
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .single();
    if (!existingProfile) {
      console.log('[Auth] No profile found after email confirmation — creating now');
      await handleNewUser(user, true);
      localStorage.setItem('newUserOnboarding', 'true');
    }
  } catch (err) {
    console.error('[Auth] ensureEmailUserProfile error:', err);
  }
}

async function setupGoogleUser(user: any) {
  try {
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .eq('id', user.id)
      .single();

    if (!existingProfile) {
      const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
      const nameParts = fullName.trim().split(' ').filter(Boolean);
      const firstName = user.user_metadata?.given_name || nameParts[0] || '';
      const lastName = user.user_metadata?.family_name || nameParts.slice(1).join(' ') || '';

      if (import.meta.env.DEV) console.log('[Auth] Extracted name from Google:', { fullName, firstName, lastName });

      user.user_metadata = {
        ...user.user_metadata,
        first_name: firstName,
        last_name: lastName,
      };

      await handleNewUser(user, true);
      localStorage.setItem('newUserOnboarding', 'true');

      localStorage.setItem('pendingUpgrade', JSON.stringify({
        tier: 'premium',
        interval: 'monthly'
      }));

      try {
        await sendWelcomeEmail(user.email || '', firstName || 'there', 'Premium');
      } catch (emailErr) {
        console.warn('[Auth] Welcome email failed:', emailErr);
      }
    } else {
      if (!existingProfile.first_name?.trim() || !existingProfile.last_name?.trim()) {
        const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
        const nameParts = fullName.trim().split(' ').filter(Boolean);
        const firstName = user.user_metadata?.given_name || nameParts[0] || '';
        const lastName = user.user_metadata?.family_name || nameParts.slice(1).join(' ') || '';

        if (firstName && lastName) {
          if (import.meta.env.DEV) console.log('[Auth] Backfilling name for existing Google user:', firstName, lastName);
          await supabase
            .from('profiles')
            .update({
              first_name: firstName,
              last_name: lastName,
              full_name: `${firstName} ${lastName}`
            })
            .eq('id', user.id);
        }
      }
    }
  } catch (err) {
    console.error('[Auth] setupGoogleUser error:', err);
  }
}


export function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingSession, setCheckingSession] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const fullUrl = window.location.href;
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const hasCodeInUrl = params.has('code');
    const hasCodeInjected = !!(window as any).__SUPABASE_AUTH_CODE__;
    const hasCode = hasCodeInUrl || hasCodeInjected;
    const hasHashTokens = hash.includes('access_token=');

    if (import.meta.env.DEV) {
      console.log('[Auth] Page loaded. URL:', fullUrl);
      console.log('[Auth] Code in URL:', hasCodeInUrl, '| Code injected by server:', hasCodeInjected, '| Has hash tokens:', hasHashTokens);
    }

    // After a successful sign-in, check whether the user arrived via a team
    // invite link. If so, send them back to the invite page to accept it.
    // Otherwise fall through to the normal dashboard redirect.
    const redirectAfterAuth = () => {
      const pendingInviteToken = sessionStorage.getItem('pendingInviteToken');
      const isNewUser = localStorage.getItem('newUserOnboarding') === 'true';
      // Always consume so a stale return URL can't leak into a later sign-in.
      const returnTo = consumeRedirectUrl();
      if (pendingInviteToken) {
        sessionStorage.removeItem('pendingInviteToken');
        window.location.href = `/team-invite/${pendingInviteToken}`;
      } else if (isNewUser) {
        localStorage.removeItem('newUserOnboarding');
        window.location.href = '/account-setup';
      } else {
        window.location.href = returnTo ?? '/dashboard';
      }
    };

    const handleAuth = async () => {
      try {
        if (hasCode) {
          // Password reset codes must be handled by ResetPassword — don't steal them here
          if (location.pathname.endsWith('reset-password')) {
            console.log('[Auth] On reset-password route — skipping code exchange, handing off to ResetPassword');
            setCheckingSession(false);
            return;
          }

          // _initialize() with detectSessionInUrl: true may have already exchanged the code
          const { data: { session: existingSession } } = await supabase.auth.getSession();
          if (existingSession?.user) {
            if (import.meta.env.DEV) console.log('[Auth] Session already established by _initialize():', existingSession.user.email);
            if (existingSession.user.app_metadata?.provider === 'google') {
              await setupGoogleUser(existingSession.user);
            } else {
              await ensureEmailUserProfile(existingSession.user);
            }
            redirectAfterAuth();
            return;
          }

          const code = params.get('code') || (window as any).__SUPABASE_AUTH_CODE__;
          if (import.meta.env.DEV) console.log('[Auth] Exchanging PKCE code...', code ? 'code found' : 'no code');

          window.history.replaceState({}, '', '/auth');

          const { data, error } = await supabase.auth.exchangeCodeForSession(code);

          if (error) {
            console.error('[Auth] Code exchange error:', error.message);
            setAuthError('Sign-in failed: ' + error.message);
            setCheckingSession(false);
            return;
          }

          if (data.session?.user) {
            if (import.meta.env.DEV) console.log('[Auth] PKCE exchange successful:', data.session.user.email);
            const user = data.session.user;
            if (user.app_metadata?.provider === 'google') {
              await setupGoogleUser(user);
            } else {
              // Email confirmation path — create profile if this is the first sign-in
              await ensureEmailUserProfile(user);
            }
            redirectAfterAuth();
            return;
          }
        }

        if (hasHashTokens) {
          if (import.meta.env.DEV) console.log('[Auth] Hash tokens detected, waiting for Supabase to process...');
          await new Promise(resolve => setTimeout(resolve, 1500));

          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            if (import.meta.env.DEV) console.log('[Auth] Session from hash tokens:', session.user.email);
            if (session.user.app_metadata?.provider === 'google') {
              await setupGoogleUser(session.user);
            }
            redirectAfterAuth();
            return;
          }
        }

        if (import.meta.env.DEV) console.log('[Auth] Checking for existing session...');
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          if (import.meta.env.DEV) console.log('[Auth] Existing session found:', session.user.email);
          window.location.href = consumeRedirectUrl() ?? '/dashboard';
          return;
        }

        if (import.meta.env.DEV) console.log('[Auth] No session found. Showing sign-in form.');
        setCheckingSession(false);

        supabase.auth.onAuthStateChange(async (event, session) => {
          if (import.meta.env.DEV) console.log('[Auth] Auth state change:', event);
          if (event === 'SIGNED_IN' && session?.user) {
            if (import.meta.env.DEV) console.log('[Auth] SIGNED_IN event received:', session.user.email);
            if (session.user.app_metadata?.provider === 'google') {
              await setupGoogleUser(session.user);
            }
            redirectAfterAuth();
          }
        });
      } catch (err) {
        console.error('[Auth] Error:', err);
        setAuthError('Something went wrong. Please try signing in again.');
        setCheckingSession(false);
      }
    };

    handleAuth();
  }, []);

  const getHeaderText = () => {
    if (location.pathname.includes('reset-password')) {
      return {
        title: 'Reset Your Password',
        subtitle: 'Enter your new password below'
      };
    }
    if (location.pathname.includes('forgot-password')) {
      return {
        title: 'Reset Password',
        subtitle: 'Enter your email to receive reset instructions'
      };
    }
    if (location.pathname.includes('signup')) {
      return {
        title: 'Create your account',
        subtitle: 'Start your 14-day free trial. No credit card required.'
      };
    }
    return {
      title: 'Welcome back',
      subtitle: 'Sign in to continue to your account'
    };
  };

  const { title, subtitle } = getHeaderText();

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand/30 to-midnight/5 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-electric mb-4" />
          <p className="text-slate">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand/30 to-midnight/5">
      <nav className="bg-white/80 backdrop-blur-sm sticky top-0 z-50 border-b border-sand/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <a href="https://sermonbuild.com" className="flex items-center">
              <img 
                src="/logo.png" 
                alt="SermonBuild" 
                className="h-8 w-auto"
              />
            </a>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-sand/20 overflow-hidden">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-midnight mb-2">{title}</h2>
              <p className="text-slate mb-6">{subtitle}</p>

              {authError && (
                <div className="mb-4 p-3 bg-crimson/10 border border-crimson/20 rounded-lg">
                  <p className="text-sm text-crimson">{authError}</p>
                </div>
              )}

              <Routes>
                <Route path="/" element={
                  <SignIn 
                    onToggleSignUp={() => navigate('/auth/signup', { replace: true })}
                    onForgotPassword={() => navigate('/auth/forgot-password')}
                  />
                } />
                
                <Route path="signup" element={
                  <SignUp onToggleSignUp={() => navigate('/auth', { replace: true })} />
                } />
                
                <Route path="forgot-password" element={
                  <ForgotPassword onBack={() => navigate('/auth')} />
                } />
                
                <Route path="reset-password" element={<ResetPassword />} />
                
                <Route path="*" element={<Navigate to="/auth" replace />} />
              </Routes>
            </div>

            <div className="px-8 py-4 bg-sand/10 border-t border-sand/20">
              <div className="text-center text-sm text-slate">
                <a 
                  href="/privacy-policy.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-electric hover:text-electric/80"
                >
                  Privacy Policy
                </a>
                {' • '}
                <a 
                  href="/terms-of-service.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-electric hover:text-electric/80"
                >
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}